import { SectionTitle } from "../../common/SectionTitle/SectionTitle";
import { styled } from "@mui/system";
import team from '../../../assets/icons/team.svg';
import arrowCircleRight from '../../../assets/icons/arrow-circle-right.svg';
import { BasicCard } from "../../common/BasicCard/BasicCard";
import subDirectoryArrowRight from '../../../assets/icons/subdirectory-arrow-right.svg';
import { List } from "../../common/List/List";
import { ListItem } from "../../common/List/ListItem";
import { motion } from 'framer-motion';
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

const OurTeamContainer = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    '@media (max-width: 1024px)': {
        padding: '0 16px',
    }
});

const ArrowCircleRight = styled('img')({
    width: '24px',
    height: '24px',
    '@media (max-width: 1024px)': {
        width: '20px',
        height: '20px',
    }
});

const CardContainer = styled('div')({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    '@media (max-width: 1024px)': {
        flexDirection: 'column',
        width: '100%',
        gap: '16px',
    }
});

const CardRow = styled('div')({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    '@media (max-width: 1024px)': {
        gap: '16px',
    }
});

const BottomContainer = styled('div')({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'center',
    gap: '47px',
    marginTop: '48px',
    '@media (max-width: 1024px)': {
        flexDirection: 'column',
        gap: '24px',
        marginTop: '32px',
    }
});

const TextContainer = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',

    '@media (max-width: 1024px)': {
        marginTop: '16px',
    }

});

const GreenText = styled('p')({
    color: '#40EA9A',
    fontSize: '16px',
    fontWeight: '700',
    lineHeight: '24px',
    '@media (max-width: 1024px)': {
        fontSize: '14px',
        lineHeight: '20px',
        textAlign: 'center',
    }
});

const SubDirectoryArrowRight = styled('img')({
    width: '64px',
    height: '64px',
    '@media (max-width: 1024px)': {
        width: '48px',
        height: '48px',
    }
});

export const OurTeam = () => {
    const { t } = useTranslation();
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 1024);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.5,
                ease: "easeOut"
            }
        }
    };

    const listItemVariants = {
        hidden: { opacity: 0, x: -20 },
        visible: {
            opacity: 1,
            x: 0,
            transition: {
                duration: 0.5,
                ease: "easeOut"
            }
        }
    };

    return (
        <OurTeamContainer>
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
            >
                <SectionTitle
                    marginBottom="48px"
                    title={t('ourTeam.title')}
                    logo={team}
                    description={t('ourTeam.description')}
                    descriptionMarginTop="24px"
                />
            </motion.div>

            <motion.div
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
            >
                {isMobile ? (
                    <CardContainer>
                        <CardRow>
                            <motion.div variants={itemVariants} style={{ width: '100%' }}>
                                <BasicCard title={t('ourTeam.steps.audit')} />
                            </motion.div>
                            <motion.div variants={itemVariants}>
                                <ArrowCircleRight src={arrowCircleRight} alt="Arrow Circle Right" />
                            </motion.div>
                            <motion.div variants={itemVariants} style={{ width: '100%' }}>
                                <BasicCard title={t('ourTeam.steps.integration')} />
                            </motion.div>
                        </CardRow>
                        <CardRow>
                            <motion.div variants={itemVariants} style={{ width: '100%' }}>
                                <BasicCard title={t('ourTeam.steps.changeManagement')} />
                            </motion.div>
                            <motion.div variants={itemVariants}>
                                <ArrowCircleRight src={arrowCircleRight} alt="Arrow Circle Right" />
                            </motion.div>
                            <motion.div variants={itemVariants} style={{ width: '100%' }}>
                                <BasicCard title={t('ourTeam.steps.support')} />
                            </motion.div>
                        </CardRow>
                    </CardContainer>
                ) : (
                    <CardContainer>
                        <motion.div variants={itemVariants} style={{ width: '100%' }}>
                            <BasicCard title={t('ourTeam.steps.audit')} />
                        </motion.div>
                        <motion.div variants={itemVariants}>
                            <ArrowCircleRight src={arrowCircleRight} alt="Arrow Circle Right" />
                        </motion.div>
                        <motion.div variants={itemVariants} style={{ width: '100%' }}>
                            <BasicCard title={t('ourTeam.steps.integration')} />
                        </motion.div>
                        <motion.div variants={itemVariants}>
                            <ArrowCircleRight src={arrowCircleRight} alt="Arrow Circle Right" />
                        </motion.div>
                        <motion.div variants={itemVariants} style={{ width: '100%' }}>
                            <BasicCard title={t('ourTeam.steps.changeManagement')} />
                        </motion.div>
                        <motion.div variants={itemVariants}>
                            <ArrowCircleRight src={arrowCircleRight} alt="Arrow Circle Right" />
                        </motion.div>
                        <motion.div variants={itemVariants} style={{ width: '100%' }}>
                            <BasicCard title={t('ourTeam.steps.support')} />
                        </motion.div>
                    </CardContainer>
                )}
            </motion.div>

            <motion.div
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
            >
                <BottomContainer>
                    <motion.div variants={itemVariants} style={{ width: isMobile ? '100%' : '35%' }}>
                        <TextContainer>
                            <GreenText>{t('ourTeam.expertise.introduction')}</GreenText>
                            {!isMobile && (
                                <SubDirectoryArrowRight src={subDirectoryArrowRight} alt="Sub Directory Arrow Right" />
                            )}
                        </TextContainer>
                    </motion.div>
                    <motion.div variants={itemVariants} >
                        <List>
                            <motion.div variants={listItemVariants}>
                                <ListItem fontSize="16px" marginBottom="16px">
                                    {t('ourTeam.expertise.items.devEx')}
                                </ListItem>
                            </motion.div>
                            <motion.div variants={listItemVariants}>
                                <ListItem fontSize="16px" marginBottom="16px">
                                    {t('ourTeam.expertise.items.cicd')}
                                </ListItem>
                            </motion.div>
                            <motion.div variants={listItemVariants}>
                                <ListItem fontSize="16px" marginBottom="16px">
                                    {t('ourTeam.expertise.items.devops')}
                                </ListItem>
                            </motion.div>
                            <motion.div variants={listItemVariants}>
                                <ListItem fontSize="16px" marginBottom="16px">
                                    {t('ourTeam.expertise.items.security')}
                                </ListItem>
                            </motion.div>
                        </List>
                    </motion.div>
                </BottomContainer>
            </motion.div>
        </OurTeamContainer>
    );
};
